import { useMemo, useState } from "react";
import { Button, Card, DataList, Modal, Table, type TableColumn } from "@knowtex-ai/components";
import type { McUsageRow } from "../../lib/mc";
import MarkdownText from "../Support/MarkdownText";
import ClampedText from "./ClampedText";
import { formatCount, formatElapsed, formatUsd, outcomeLabel, spanMs } from "./format";
import type { McMission } from "./runData";
import TimeCell from "./TimeCell";
import { useRetained } from "./useRetained";

const MissionResult = ({ mission, live }: { mission: McMission; live: boolean }) => {
  const outcome = outcomeLabel(mission.outcome);
  if (outcome) return <span>{outcome}</span>;
  if (mission.finishedAt) return <span>{mission.status || "Finished"}</span>;
  return <span>{live ? "Running" : "Didn't finish"}</span>;
};

interface RunMissionsProps {
  missions: McMission[];
  live: boolean;
  /** Cost per mission id; omitted when MC doesn't report usage */
  usage?: McUsageRow[];
}

export default function RunMissions({ missions, live, usage }: RunMissionsProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = missions.find((mission) => mission.id === openId) ?? null;
  const shown = useRetained(open);
  const columns = useMemo((): TableColumn<McMission>[] => {
    const cost = (mission: McMission) => usage?.find((row) => row.key === mission.id)?.costUsd;
    const elapsed = (mission: McMission) => spanMs(mission.startedAt, mission.finishedAt, live ? Date.now() : null);
    return [
      { id: "id", header: "Mission", width: 90, accessor: "id", cell: (m) => <span className="font-mono">{m.id}</span> },
      {
        id: "objective",
        header: "Objective",
        width: 440,
        grow: true,
        accessor: "objective",
        cell: (m) => <ClampedText text={m.objective || "—"} lines={2} />,
      },
      { id: "result", header: "Result", width: 130, accessor: "outcome", cell: (m) => <MissionResult mission={m} live={live} /> },
      { id: "started", header: "Started", width: 240, cell: (m) => <TimeCell iso={m.startedAt} /> },
      { id: "duration", header: "Duration", width: 100, align: "end", accessor: elapsed, cell: (m) => formatElapsed(elapsed(m)) },
      ...(usage
        ? [
            {
              id: "cost",
              header: "Cost",
              width: 90,
              align: "end" as const,
              accessor: cost,
              cell: (m: McMission) => {
                const usd = cost(m);
                return usd === undefined ? "—" : formatUsd(usd);
              },
            },
          ]
        : []),
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 88,
        sticky: "end",
        hideable: false,
        cell: (m) => (
          <Button variant="secondary" size="small" onClick={() => setOpenId(m.id)} aria-label={`View mission ${m.id}`}>
            View
          </Button>
        ),
      },
    ];
  }, [live, usage]);

  return (
    <Card variant="elevated" title="Missions" subtitle={`${formatCount(missions.length)} started`}>
      <Table
        aria-label="Missions"
        size="small"
        columns={columns}
        data={missions}
        getRowId={(m) => m.id}
        paginated={false}
        emptyState={live ? "No missions yet." : "No missions recorded."}
      />
      <Modal
        open={openId !== null}
        onOpenChange={(next) => !next && setOpenId(null)}
        title={shown ? `Mission ${shown.id}` : "Mission"}
        size="large"
      >
        {shown && (
          <DataList
            size="small"
            aria-label={`Mission ${shown.id}`}
            items={[
              { id: "result", label: "Result", value: <MissionResult mission={shown} live={live} /> },
              { id: "started", label: "Started", value: <TimeCell iso={shown.startedAt} /> },
              { id: "finished", label: "Finished", value: <TimeCell iso={shown.finishedAt} /> },
              {
                id: "objective",
                label: "Objective",
                value: <p className="whitespace-pre-wrap break-words">{shown.objective || "—"}</p>,
              },
              {
                id: "summary",
                label: "Summary",
                value: shown.result ? <MarkdownText text={shown.result} compact /> : live ? "Not finished yet" : "—",
              },
            ]}
          />
        )}
      </Modal>
    </Card>
  );
}
